// Deal Submission spec §10 — System-Admin list page for
// DealSubmissionTemplate. Parallel to /admin/collection-templates,
// gated by DealSubmission.ManageTemplates.

import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, Plus, Power } from "lucide-react";
import { Badge, Button } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { useToast } from "@/components/ui/Toast";
import {
  dealSubmissionTemplatesApi,
  type DealSubmissionTemplateDto,
} from "@/api/dealSubmissionTemplates";

// Hoisted so the two lifecycle mutations invalidate the SAME prefix the list
// reads under, from one place, instead of each restating the literal.
const TEMPLATES_KEY = ["deal-submission-templates-admin"];

export function DealSubmissionTemplatesPage() {
  // Round-3 #7 — External Collection › Deal Templates.
  useBreadcrumbs([{ label: "External Collection" }, { label: "Deal Templates" }]);
  const qc = useQueryClient();
  const { toast } = useToast();
  const [showInactive, setShowInactive] = useState(false);

  const list = useQuery({
    queryKey: [...TEMPLATES_KEY, showInactive],
    queryFn: () => dealSubmissionTemplatesApi.list(showInactive),
  });

  const invalidateTemplates = () =>
    void qc.invalidateQueries({ queryKey: TEMPLATES_KEY });

  const deactivate = useMutation({
    mutationFn: (id: string) => dealSubmissionTemplatesApi.deactivate(id),
    onSuccess: () => {
      toast({ tone: "success", message: "Template deactivated." });
      invalidateTemplates();
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  // The way back. Deactivating the last active template of an origin 503s
  // broker deal submission and blocks buyer escalation, and an inactive
  // template cannot be edited — so until this existed, the only recovery was a
  // hand-run database update.
  const activate = useMutation({
    mutationFn: (id: string) => dealSubmissionTemplatesApi.activate(id),
    onSuccess: () => {
      toast({ tone: "success", message: "Template reactivated." });
      invalidateTemplates();
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  const rows = list.data ?? [];

  const columns: Column<DealSubmissionTemplateDto>[] = [
    {
      key: "name",
      header: "Template",
      render: (t) => (
        <Link
          to={`/admin/deal-submission-templates/${t.id}`}
          className="font-semibold text-onyx hover:text-gold-shadow"
        >
          <div className="flex items-center gap-space-2">
            <span>{t.name}</span>
            {!t.active && <Badge status="archived">Inactive</Badge>}
            <Badge status="info">v{t.version}</Badge>
          </div>
          {t.description && (
            <div className="text-caption text-gray-mid font-normal max-w-md truncate">
              {t.description}
            </div>
          )}
        </Link>
      ),
    },
    {
      key: "sections",
      header: "Sections",
      render: (t) => (
        <span className="text-small text-onyx">{t.sections.length}</span>
      ),
    },
    {
      key: "documents",
      header: "Documents",
      render: (t) => (
        <span className="text-small text-onyx">
          {t.documentRequests.length}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (t) =>
        t.active ? (
          <Button
            variant="secondary"
            size="sm"
            type="button"
            disabled={deactivate.isPending}
            onClick={() => {
              if (
                confirm(
                  `Deactivate '${t.name}'? In-flight submissions stay on their pinned version.`,
                )
              ) {
                deactivate.mutate(t.id);
              }
            }}
          >
            <Archive size={14} className="mr-space-1" /> Deactivate
          </Button>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            type="button"
            disabled={activate.isPending}
            onClick={() => {
              if (
                confirm(
                  `Reactivate '${t.name}' v${t.version}? If another template is already ` +
                    `active for the same origin, only one of them will be reachable.`,
                )
              ) {
                activate.mutate(t.id);
              }
            }}
          >
            <Power size={14} className="mr-space-1" /> Reactivate
          </Button>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader
        eyebrow="Admin · Deal Flow"
        title="Deal Submission templates"
        description="Broker deal-submission wizard layouts."
        actions={
          <Link to="/admin/deal-submission-templates/new">
            <Button type="button">
              <Plus size={16} className="mr-space-1" /> New template
            </Button>
          </Link>
        }
      />

      <div className="flex items-end gap-space-3">
        <label className="flex items-center gap-space-2 text-small text-onyx">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show inactive versions
        </label>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(t) => t.id}
        loading={list.isLoading}
        error={list.error instanceof Error ? list.error.message : null}
        emptyMessage="No templates yet. Click 'New template' to author one."
      />
    </div>
  );
}
